/**
 * ThemeProvider
 * =============
 * Minimal light/dark/system theme switcher. We avoid `next-themes`
 * because we're on Vite, and the existing logic is small enough that
 * a 60-line provider beats pulling another dep.
 *
 * Initial paint is handled by an inline `<script>` in index.html that
 * sets the `dark` class on <html> BEFORE React boots -- otherwise
 * dark-mode users see a white flash. This provider takes over from
 * there and persists the user's choice to localStorage.
 */
import {
  createContext, useCallback, useContext, useEffect, useState,
  type PropsWithChildren,
} from "react";

type Theme = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "pbmc-theme";

interface ThemeContextValue {
  theme: Theme;
  // The actual class applied to <html> after resolving "system" against
  // the OS preference. Use this when you need to branch on the real
  // current appearance (e.g. picking a different image).
  resolved: ResolvedTheme;
  setTheme: (t: Theme) => void;
  // Cycle helper for a single-icon toggle button (light -> dark -> system -> light).
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStored(): Theme {
  if (typeof window === "undefined") return "system";
  const v = window.localStorage.getItem(STORAGE_KEY);
  if (v === "light" || v === "dark" || v === "system") return v;
  return "system";
}

function resolve(theme: Theme): ResolvedTheme {
  if (theme === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return theme;
}

export function ThemeProvider({ children }: PropsWithChildren) {
  const [theme, setThemeState] = useState<Theme>(() => readStored());
  const [resolved, setResolved] = useState<ResolvedTheme>(() =>
    resolve(readStored()));

  // Apply the resolved theme to <html> + persist the user's choice.
  useEffect(() => {
    const r = resolve(theme);
    setResolved(r);
    const root = document.documentElement;
    root.classList.toggle("dark", r === "dark");
    try { window.localStorage.setItem(STORAGE_KEY, theme); } catch {
      // localStorage may throw in private mode -- fine, we just won't
      // remember across reloads.
    }
  }, [theme]);

  // React to OS-level theme changes when the user picked "system".
  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => setResolved(mq.matches ? "dark" : "light");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const toggleTheme = useCallback(() => {
    setThemeState((curr) =>
      curr === "light" ? "dark" : curr === "dark" ? "system" : "light",
    );
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
